import { Controller } from '@hotwired/stimulus'
import { lastLoadedItem } from '../utils/grid_items'

// Checking products in organize mode, to file them under a section. The whole card is clickable
// (its links are inert in this mode) — the checkmark at the bottom right is only an indicator.
//
// The filing itself lives in a PAGE (/list_sections/rangement): this controller only opens it, in
// the layout's "modal" frame, with the checked products as parameters.
export default class extends Controller {
  static targets = ['item', 'pill', 'count', 'trigger', 'resetBeacon']

  #resetForCache = () => this.clear()

  // Same hygiene as sortable: a selection is transient state, a Turbo snapshot must not bring it
  // back on a later visit.
  connect() {
    document.addEventListener('turbo:before-cache', this.#resetForCache)
  }

  disconnect() {
    document.removeEventListener('turbo:before-cache', this.#resetForCache)
  }

  // Leaving organize mode drops the selection. Entering it needs no counterpart: the mode is READ
  // from the DOM (see #organizing) rather than remembered here — on a page opened with
  // ?organize=true, sortable activates during its own connect, and the activation event sometimes
  // fired before this action was wired, leaving the selection inert until the next round trip.
  deactivate() {
    this.clear()
  }

  toggle(event) {
    if (!this.#organizing) return
    // The drag handle shares the card: a missed grab must not check it.
    if (event.target.closest('[data-sortable-handle]')) return

    event.currentTarget.toggleAttribute('data-selected')
    // Grain of the gesture on native: same feedback as selecting a tab.
    window.dispatchEvent(new CustomEvent('haptic:trigger', { detail: { type: 'selection' } }))
    this.#refresh()
  }

  open() {
    const url = new URL(this.triggerTarget.dataset.groupingUrl, window.location.origin)
    this.#selected.forEach((card) => {
      url.searchParams.append('product_ids[]', card.id.replace(/^product_/, ''))
    })
    // The grid only holds a prefix of the list (infinite pagination) whereas grouping reasons about
    // the complete server order. The server needs to know how far the DOM goes to decide whether it
    // can mirror the move there or must hand the list back (see ListSectionsController).
    const lastItem = this.#lastLoadedItem
    if (lastItem) url.searchParams.set('last_item_id', lastItem.id)

    // The "modal" frame is only rendered on the web (cf. layouts/application): its
    // presence says where the page must land — on native the app presents it as a
    // native modal in its own webview instead.
    const framed = document.querySelector('turbo-frame#modal')
    window.Turbo.visit(url.toString(), framed ? { frame: 'modal' } : {})
  }

  // Deleting a section frees its products to the vrac, so its form needs the same loaded window as
  // the filing flow. Stamped on grid:rendered — that is, on the initial load AND after every added
  // page — rather than on submit, where the listener order against Turbo would be a guess.
  stampWindow() {
    const lastItem = this.#lastLoadedItem
    if (!lastItem) return

    this.element
      .querySelectorAll('input[name="last_item_id"]')
      .forEach((field) => (field.value = lastItem.id))
  }

  // A filing went through: its turbo_streams re-rendered the moved cards (unchecked) and dropped
  // this beacon, the only signal that reaches us here.
  resetBeaconTargetConnected(beacon) {
    beacon.remove()
    this.clear()
  }

  clear() {
    this.itemTargets.forEach((card) => card.removeAttribute('data-selected'))
    this.#refresh()
  }

  // The floating bar only exists on the web: on native the action lives in the
  // toolbar, and bridge--list-actions listens for the event below.
  #refresh() {
    const count = this.#selected.length
    this.dispatch('changed', { detail: { count } })
    if (!this.hasPillTarget) return

    // Toggling hidden ↔ flex: with no explicit display class, removing hidden would fall back to
    // display:block.
    this.pillTarget.classList.toggle('hidden', count === 0)
    this.pillTarget.classList.toggle('flex', count > 0)
    if (count > 0) {
      const label = count === 1 ? this.countTarget.dataset.one : this.countTarget.dataset.other
      this.countTarget.textContent = `${count} ${label}`
    }
  }

  get #selected() {
    return this.itemTargets.filter((card) => card.hasAttribute('data-selected'))
  }

  // Last product/section actually loaded in the grid.
  get #lastLoadedItem() {
    return lastLoadedItem(this.element.querySelector('#items-load'))
  }

  // Organize mode: sortable puts data-sortable on the grid and on the action-bar buttons — its
  // presence is the only source of truth.
  get #organizing() {
    return this.element.querySelector('[data-sortable]') !== null
  }
}
